import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Car,
  CheckCircle2,
  DownloadCloud,
  Eye,
  Loader2,
  MoreVertical,
  RefreshCcw,
  Search,
  Trash2,
  Undo2,
  UploadCloud,
} from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError, API_BASE, TOKEN_KEY } from "@/lib/api";
import { formatUSD, formatMiles } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  PageContainer,
  PageHeader,
  StatusPill,
  Skeleton,
  EmptyState,
  SYNC_SOURCE_LABEL,
  relativeTime,
} from "./dealerUi";

const STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "available", label: "Available" },
  { key: "pending", label: "Pending" },
  { key: "sold", label: "Sold" },
];

const SORT_OPTIONS = [
  { value: "newest", label: "Newest listing" },
  { value: "oldest", label: "Oldest listing" },
  { value: "price_asc", label: "Price · low → high" },
  { value: "price_desc", label: "Price · high → low" },
  { value: "most_leads", label: "Most leads" },
  { value: "most_views", label: "Most views" },
];

const statusPill = (status) => {
  if (status === "available") return <StatusPill tone="emerald">Available</StatusPill>;
  if (status === "sold") return <StatusPill tone="slate">Sold</StatusPill>;
  return <StatusPill tone="amber">Pending</StatusPill>;
};

const MarkSoldDialog = ({ vehicle, open, onOpenChange, onConfirm, saving }) => {
  const [price, setPrice] = useState("");
  useEffect(() => {
    if (open && vehicle) setPrice(String(vehicle.price ?? ""));
  }, [open, vehicle]);
  if (!vehicle) return null;
  const parsed = parseFloat(price);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="mark-sold-dialog">
        <DialogHeader>
          <DialogTitle>Mark this vehicle as sold?</DialogTitle>
          <DialogDescription>
            {vehicle.year} {vehicle.make} {vehicle.model} {vehicle.trim || ""}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="sale-price">Final sale price</Label>
          <Input
            id="sale-price"
            type="number"
            min={0}
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            data-testid="mark-sold-price-input"
          />
          <p className="text-xs text-slate-500">
            Defaults to the current listing price ({formatUSD(vehicle.price)}). Override if the final agreed price differs.
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            className="bg-brand hover:bg-brand-hover text-white"
            onClick={() => onConfirm(isNaN(parsed) ? vehicle.price : parsed)}
            disabled={saving || isNaN(parsed) || parsed <= 0}
            data-testid="mark-sold-confirm-btn"
          >
            {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : "Confirm sold"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ImportPanel = ({ open, onOpenChange, onDone }) => {
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [syncingSource, setSyncingSource] = useState(null);

  const refreshHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get("/dealer/inventory/sync/history");
      setHistory(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) refreshHistory();
  }, [open, refreshHistory]);

  const downloadTemplate = async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    const url = `${API_BASE}/dealer/inventory/sync/csv-template`;
    try {
      const resp = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error("Failed to download template");
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "belomotors_inventory_template.csv";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error(e.message || "Template download failed");
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/dealer/inventory/sync/csv-upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.rows_added} new · ${data.rows_updated} updated · ${data.rows_skipped} skipped`);
      onDone?.();
      refreshHistory();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (e.target) e.target.value = "";
    }
  };

  const triggerMockSync = async (source) => {
    setSyncingSource(source);
    try {
      const { data } = await api.post("/dealer/inventory/sync/mock-crm", { source });
      toast.success(`${SYNC_SOURCE_LABEL[source]}: ${data.rows_added} added · ${data.rows_updated} updated`);
      onDone?.();
      refreshHistory();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSyncingSource(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="import-panel-dialog">
        <DialogHeader>
          <DialogTitle>Import inventory</DialogTitle>
          <DialogDescription>
            Sync from your existing DMS or upload a CSV. Rows are matched by VIN.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { source: "vinsolutions", label: "VinSolutions" },
            { source: "dealercenter", label: "DealerCenter" },
            { source: "vauto", label: "vAuto" },
          ].map((cfg) => (
            <button
              key={cfg.source}
              onClick={() => triggerMockSync(cfg.source)}
              disabled={syncingSource === cfg.source}
              className="text-left rounded-xl border border-slate-200 hover:border-brand p-4 transition-colors group disabled:opacity-60"
              data-testid={`sync-btn-${cfg.source}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-800">{cfg.label}</span>
                {syncingSource === cfg.source ? (
                  <Loader2 size={16} className="animate-spin text-brand" />
                ) : (
                  <RefreshCcw size={16} className="text-slate-400 group-hover:text-brand" />
                )}
              </div>
              <p className="mt-1 text-xs text-slate-500">One-click sync (mocked feed)</p>
            </button>
          ))}
          <div className="rounded-xl border border-slate-200 p-4">
            <p className="font-semibold text-slate-800 flex items-center gap-2">
              <UploadCloud size={16} className="text-slate-500" /> CSV upload
            </p>
            <p className="mt-1 text-xs text-slate-500">One-time upload. Matches on VIN.</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <label
                className="inline-flex items-center gap-2 rounded-md bg-brand text-white px-3 py-1.5 text-sm cursor-pointer hover:bg-brand-hover"
                data-testid="csv-upload-btn"
              >
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />}
                Upload CSV
                <input
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={handleUpload}
                  disabled={uploading}
                />
              </label>
              <Button
                variant="outline"
                size="sm"
                onClick={downloadTemplate}
                data-testid="csv-template-btn"
              >
                <DownloadCloud size={14} /> Template
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-2">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2">
            Recent sync history
          </p>
          {historyLoading ? (
            <Skeleton className="h-24" />
          ) : history.length === 0 ? (
            <p className="text-sm text-slate-500">No syncs yet.</p>
          ) : (
            <div className="max-h-56 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
              {history.map((row) => (
                <div key={row.id} className="px-3 py-2 flex items-center justify-between text-sm">
                  <div>
                    <p className="font-medium text-slate-800">
                      {SYNC_SOURCE_LABEL[row.source] || row.source}
                    </p>
                    <p className="text-xs text-slate-500">
                      {row.rows_added} added · {row.rows_updated} updated · {row.rows_skipped} skipped
                    </p>
                  </div>
                  <div className="text-right">
                    <StatusPill tone={row.status === "success" ? "emerald" : row.status === "failed" ? "rose" : "amber"}>
                      {row.status}
                    </StatusPill>
                    <p className="text-[11px] text-slate-400 mt-1">{relativeTime(row.finished_at || row.started_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const EditPriceDialog = ({ vehicle, open, onOpenChange, onSave, saving }) => {
  const [price, setPrice] = useState("");
  const [mileage, setMileage] = useState("");
  useEffect(() => {
    if (open && vehicle) {
      setPrice(String(vehicle.price ?? ""));
      setMileage(String(vehicle.mileage ?? ""));
    }
  }, [open, vehicle]);
  if (!vehicle) return null;
  const parsed = parseFloat(price);
  const parsedMileage = parseInt(mileage, 10);
  const validPrice = !isNaN(parsed) && parsed > 0;
  const validMileage = !isNaN(parsedMileage) && parsedMileage >= 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="edit-price-dialog">
        <DialogHeader>
          <DialogTitle>Adjust listing</DialogTitle>
          <DialogDescription>
            {vehicle.year} {vehicle.make} {vehicle.model} {vehicle.trim || ""}
            <span className="block mt-1 text-[11px] text-slate-500">
              Only Price and Mileage are dealer-editable. Anything else must be updated by BeloMotors admin.
            </span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="new-price">New price (USD)</Label>
            <Input
              id="new-price"
              type="number"
              min={0}
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              data-testid="edit-price-input"
            />
            <p className="text-xs text-slate-500">Current: {formatUSD(vehicle.price)}</p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="new-mileage">Mileage</Label>
            <Input
              id="new-mileage"
              type="number"
              min={0}
              value={mileage}
              onChange={(e) => setMileage(e.target.value)}
              data-testid="edit-mileage-input"
            />
            <p className="text-xs text-slate-500">Current: {(vehicle.mileage || 0).toLocaleString()} mi</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button
            className="bg-brand text-white hover:bg-brand-hover"
            onClick={() => onSave({ price: parsed, mileage: parsedMileage })}
            disabled={saving || !validPrice || !validMileage}
            data-testid="edit-price-save-btn"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : null} Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DealerInventory = () => {
  const [query, setQuery] = useState({ status: "all", q: "", sort: "newest" });
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [inv, setInv] = useState({ items: [], total: 0, page: 1, page_size: 25 });
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);
  const [marking, setMarking] = useState(null); // vehicle object
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(query.q.trim()), 300);
    return () => clearTimeout(t);
  }, [query.q]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const params = {
          status: query.status,
          sort: query.sort,
          page,
          page_size: pageSize,
        };
        if (debouncedQ) params.q = debouncedQ;
        const { data } = await api.get("/dealer/inventory", { params });
        if (alive) setInv(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [query.status, query.sort, debouncedQ, page, pageSize, refreshTick]);

  const refresh = () => setRefreshTick((n) => n + 1);

  const doMarkSold = async (salePrice) => {
    if (!marking) return;
    setSaving(true);
    try {
      await api.post(`/dealer/inventory/${marking.id}/mark-sold`, { sale_price: salePrice });
      toast.success("Vehicle marked as sold.");
      setMarking(null);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const doRelist = async (vehicle) => {
    try {
      await api.post(`/dealer/inventory/${vehicle.id}/relist`);
      toast.success("Vehicle re-listed.");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const doDelete = async (vehicle) => {
    if (!window.confirm(`Remove ${vehicle.year} ${vehicle.make} ${vehicle.model} from inventory?`)) return;
    try {
      await api.delete(`/dealer/inventory/${vehicle.id}`);
      toast.success("Vehicle removed.");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const doSavePrice = async (updates) => {
    // Phase 11 — dealer edit dialog now sends both price + mileage. Backend
    // enforces the field allow-list; anything else 400s.
    if (!editing) return;
    setSaving(true);
    try {
      const body = {};
      if (typeof updates?.price === "number" && !isNaN(updates.price)) body.price = updates.price;
      if (typeof updates?.mileage === "number" && !isNaN(updates.mileage)) body.mileage = updates.mileage;
      await api.patch(`/dealer/inventory/${editing.id}`, body);
      toast.success("Listing updated.");
      setEditing(null);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(inv.total / pageSize));

  const rows = useMemo(() => inv.items || [], [inv]);

  return (
    <PageContainer>
      <PageHeader
        title="Inventory"
        subtitle={`${inv.total} vehicle${inv.total === 1 ? "" : "s"} on your lot`}
        testId="dealer-inventory-header"
        right={
          <>
            <Button
              variant="outline"
              onClick={refresh}
              data-testid="inventory-refresh-btn"
            >
              <RefreshCcw size={15} /> Refresh
            </Button>
            <Button
              className="bg-brand text-white hover:bg-brand-hover"
              onClick={() => setImportOpen(true)}
              data-testid="inventory-import-btn"
            >
              <UploadCloud size={15} /> Import
            </Button>
          </>
        }
      />

      {/* Filter bar */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-col md:flex-row md:items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search VIN, make, model, trim…"
            value={query.q}
            onChange={(e) => {
              setQuery((q) => ({ ...q, q: e.target.value }));
              setPage(1);
            }}
            className="pl-9"
            data-testid="inventory-search-input"
          />
        </div>
        <Tabs
          value={query.status}
          onValueChange={(v) => {
            setQuery((q) => ({ ...q, status: v }));
            setPage(1);
          }}
        >
          <TabsList data-testid="inventory-status-tabs">
            {STATUS_TABS.map((tab) => (
              <TabsTrigger key={tab.key} value={tab.key} data-testid={`inventory-tab-${tab.key}`}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <Select
          value={query.sort}
          onValueChange={(v) => setQuery((q) => ({ ...q, sort: v }))}
        >
          <SelectTrigger className="w-56" data-testid="inventory-sort-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-12" />)}
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Car}
            title="No vehicles match your filters"
            description="Try clearing filters, or import inventory from your DMS."
            action={<Button onClick={() => setImportOpen(true)} className="bg-brand text-white">Import inventory</Button>}
            testId="inventory-empty-state"
          />
        ) : (
          <Table data-testid="inventory-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Vehicle</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Mileage</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Days on lot</TableHead>
                <TableHead>Leads · Views</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((v) => (
                <TableRow key={v.id} data-testid={`inventory-row-${v.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-14 h-10 rounded-md overflow-hidden bg-slate-100 flex-shrink-0">
                        {v.images?.[0] && (
                          <img src={v.images[0]} alt="" className="w-full h-full object-cover" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-800 text-sm">
                          {v.year} {v.make} {v.model} {v.trim || ""}
                        </p>
                        <p className="text-xs text-slate-500 truncate">
                          {v.exterior_color} · VIN …{v.vin?.slice(-6)}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <p className="font-semibold text-slate-800">{formatUSD(v.price)}</p>
                    {v.previous_price && (
                      <p className="text-[11px] text-slate-400 line-through">
                        {formatUSD(v.previous_price)}
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-slate-700">{formatMiles(v.mileage)}</TableCell>
                  <TableCell>{statusPill(v.status)}</TableCell>
                  <TableCell className="text-xs text-slate-600">
                    {SYNC_SOURCE_LABEL[v.sync_source] || v.sync_source}
                  </TableCell>
                  <TableCell className="text-sm text-slate-700">{v.days_on_lot}d</TableCell>
                  <TableCell className="text-sm text-slate-700">
                    {v.lead_count} · {v.views_count}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          data-testid={`inventory-actions-${v.id}`}
                        >
                          <MoreVertical size={16} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <a
                            href={`/vehicle/${v.id}`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`inventory-view-public-${v.id}`}
                          >
                            <Eye size={14} /> View public page
                          </a>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setEditing(v)} data-testid={`inventory-edit-price-${v.id}`}>
                          Edit price
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {v.status !== "sold" ? (
                          <DropdownMenuItem
                            onClick={() => setMarking(v)}
                            data-testid={`inventory-mark-sold-${v.id}`}
                          >
                            <CheckCircle2 size={14} /> Mark sold
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={() => doRelist(v)} data-testid={`inventory-relist-${v.id}`}>
                            <Undo2 size={14} /> Re-list
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => doDelete(v)}
                          className="text-rose-600 focus:text-rose-700"
                          data-testid={`inventory-delete-${v.id}`}
                        >
                          <Trash2 size={14} /> Remove listing
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Pagination */}
      {rows.length > 0 && totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
          <span>Page {inv.page} of {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}

      <ImportPanel open={importOpen} onOpenChange={setImportOpen} onDone={refresh} />
      <MarkSoldDialog
        vehicle={marking}
        open={!!marking}
        onOpenChange={(v) => !v && setMarking(null)}
        onConfirm={doMarkSold}
        saving={saving}
      />
      <EditPriceDialog
        vehicle={editing}
        open={!!editing}
        onOpenChange={(v) => !v && setEditing(null)}
        onSave={doSavePrice}
        saving={saving}
      />
    </PageContainer>
  );
};

export default DealerInventory;
